import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Footprints, Trophy, Medal, Crown, Flame, Target,
  TrendingUp, CalendarDays, Star, Zap, MapPin,
  Eye, EyeOff, Loader2, Check, X, ChevronRight, Lock,
  Swords, Gift, Sparkles,
} from 'lucide-react';
import GoogleFitConnect from '../components/GoogleFitConnect';

const GOAL = 10000;

/* ─── Badge icon map ─── */
const BADGE_ICONS = {
  footprints: Footprints,
  flame: Flame,
  trophy: Trophy,
  medal: Medal,
  crown: Crown,
};
const TIER_COLORS = {
  bronze: 'from-amber-700 to-amber-600',
  silver: 'from-zinc-400 to-zinc-300',
  gold: 'from-amber-500 to-yellow-400',
  diamond: 'from-cyan-400 to-blue-500',
};
const TIER_RING = {
  bronze: 'ring-amber-700/40',
  silver: 'ring-zinc-400/40',
  gold: 'ring-amber-400/40',
  diamond: 'ring-cyan-400/40',
};

/* ─── Animated circle progress ─── */
function HeroCircle({ steps, size = 160, strokeWidth = 10 }) {
  const pct = Math.min(100, (steps / GOAL) * 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const goalReached = steps >= GOAL;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <defs>
          <linearGradient id="hero-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={goalReached ? '#f59e0b' : '#3b82f6'} />
            <stop offset="100%" stopColor={goalReached ? '#ef4444' : '#8b5cf6'} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-white/[0.04]" />
        <motion.circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="url(#hero-grad)" strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Footprints className={`w-5 h-5 mb-1 ${goalReached ? 'text-amber-400' : 'text-blue-400'}`} />
        <span className="text-3xl font-black tracking-tight">{steps >= 1000 ? `${(steps / 1000).toFixed(1)}k` : steps}</span>
        <span className="text-[10px] text-muted-foreground uppercase tracking-widest">/ 10k</span>
      </div>
    </div>
  );
}

/* ─── Rank helpers ─── */
function RankIcon({ rank }) {
  if (rank === 1) return <Crown className="w-5 h-5 text-amber-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-zinc-300" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
  return <span className="w-5 text-center text-xs font-bold text-muted-foreground">{rank}</span>;
}
function getRankBg(rank) {
  if (rank === 1) return 'bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border-amber-500/20';
  if (rank === 2) return 'bg-gradient-to-r from-zinc-400/10 to-zinc-500/5 border-zinc-400/20';
  if (rank === 3) return 'bg-gradient-to-r from-amber-700/10 to-orange-600/5 border-amber-700/20';
  return 'bg-card/40 border-border/30';
}

/* ─── Tab button ─── */
function TabBtn({ active, children, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${active ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'bg-card/50 text-muted-foreground hover:text-foreground'}`}
    >
      {children}
    </button>
  );
}

/* ════════════════════════════════════════════ */
export default function ExtraMilesPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';

  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showInput, setShowInput] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving] = useState(false);

  const [isVisible, setIsVisible] = useState(true);
  const [toggling, setToggling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, histRes, lbRes, userRes, challengeRes] = await Promise.all([
        api.get('/steps/stats'),
        api.get('/steps/history?days=7'),
        api.get('/steps/leaderboard'),
        api.get('/auth/me'),
        api.get('/steps/weekly-challenge').catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setHistory(histRes.data);
      setLeaderboard(lbRes.data);
      setIsVisible(userRes.data.leaderboard_visible !== false);
      if (challengeRes.data) setChallenge(challengeRes.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Log steps ─── */
  const handleSave = async () => {
    const val = parseInt(inputVal);
    if (isNaN(val) || val < 0) { toast.error(fr ? 'Nombre invalide' : 'Invalid number'); return; }
    setSaving(true);
    try {
      const res = await api.post('/steps/log', { steps: val });
      if (res.data.xp_awarded > 0) {
        toast.success(fr ? `+${res.data.xp_awarded} XP! Objectif 10k atteint!` : `+${res.data.xp_awarded} XP! 10k goal reached!`);
      } else if (res.data.goal_reached) {
        toast.success(fr ? 'Objectif 10k atteint!' : '10k goal reached!');
      } else {
        toast.success(fr ? 'Pas enregistres!' : 'Steps logged!');
      }
      setShowInput(false);
      setInputVal('');
      fetchData();
    } catch { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const toggleVisibility = async () => {
    setToggling(true);
    try {
      const res = await api.put('/steps/privacy');
      setIsVisible(res.data.leaderboard_visible);
    } catch { /* silent */ } finally { setToggling(false); }
  };

  const claimChallenge = async () => {
    setClaiming(true);
    try {
      const res = await api.post('/steps/weekly-challenge/claim');
      toast.success(fr ? `+${res.data.xp_awarded} XP! Défi complété!` : `+${res.data.xp_awarded} XP! Challenge complete!`);
      setChallenge(prev => prev ? { ...prev, claimed: true } : prev);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setClaiming(false); }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  }

  const todaySteps = stats?.today?.steps || 0;
  const goalReached = todaySteps >= GOAL;
  const streak = stats?.streak || 0;
  const pr = stats?.personal_record || {};
  const monthStats = stats?.month_stats || {};
  const allTime = stats?.all_time || {};
  const earned = stats?.badges?.earned || [];
  const available = stats?.badges?.available || [];
  const earnedIds = new Set(earned.map(b => b.id));

  return (
    <Layout>
      <div className="max-w-lg mx-auto pb-24 p-4 space-y-5" data-testid="extra-miles-page">

        {/* ═══ Header ═══ */}
        <div>
          <h1 className="text-xl font-heading uppercase tracking-wider flex items-center gap-2.5" data-testid="extra-miles-title">
            <Footprints className="w-6 h-6 text-blue-400" />
            Extra Miles
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {fr ? '10 000 pas par jour. Pas d\'excuses.' : '10,000 steps a day. No excuses.'}
          </p>
        </div>

        {/* ═══ Hero Card ═══ */}
        <Card className={`border-0 shadow-xl overflow-hidden ${goalReached ? 'bg-gradient-to-br from-amber-500/10 via-card to-orange-500/5' : 'bg-gradient-to-br from-blue-500/8 via-card to-indigo-500/5'}`} data-testid="hero-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-5">
              <HeroCircle steps={todaySteps} />
              <div className="flex-1 space-y-2.5">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">
                    {fr ? "Aujourd'hui" : 'Today'}
                  </p>
                  <p className="text-2xl font-black">{todaySteps.toLocaleString()} <span className="text-sm text-muted-foreground font-normal">{fr ? 'pas' : 'steps'}</span></p>
                </div>
                {goalReached ? (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                    <span className="text-xs font-bold text-amber-400">{fr ? 'Objectif atteint!' : 'Goal reached!'}</span>
                  </div>
                ) : (
                  <p className="text-[10px] text-muted-foreground">{(GOAL - todaySteps).toLocaleString()} {fr ? 'pas restants' : 'steps remaining'}</p>
                )}

                {/* Streak & PR pills */}
                <div className="flex flex-wrap gap-1.5">
                  {streak > 0 && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 text-[10px] font-bold" data-testid="streak-badge">
                      <Flame className="w-3 h-3" /> {streak} {fr ? 'jours' : 'days'}
                    </div>
                  )}
                  {pr.steps > 0 && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400 text-[10px] font-bold" data-testid="pr-badge">
                      <Star className="w-3 h-3" /> PR {pr.steps >= 1000 ? `${(pr.steps / 1000).toFixed(1)}k` : pr.steps}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick input */}
            <div className="mt-4">
              <AnimatePresence mode="wait">
                {showInput ? (
                  <motion.div key="input" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="flex items-center gap-2">
                    <Input type="number" placeholder={fr ? 'Nombre de pas' : 'Step count'} value={inputVal} onChange={(e) => setInputVal(e.target.value)}
                      className="flex-1 text-center font-bold" autoFocus onKeyDown={(e) => e.key === 'Enter' && handleSave()} data-testid="steps-input" />
                    <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="steps-save-btn">
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowInput(false)}><X className="w-3.5 h-3.5" /></Button>
                  </motion.div>
                ) : (
                  <motion.div key="btn" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => { setShowInput(true); setInputVal(todaySteps > 0 ? String(todaySteps) : ''); }} data-testid="steps-log-btn">
                      <Footprints className="w-3.5 h-3.5 mr-1.5" />
                      {todaySteps > 0 ? (fr ? 'Mettre a jour mes pas' : 'Update my steps') : (fr ? 'Entrer mes pas' : 'Log my steps')}
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </CardContent>
        </Card>

        {/* ═══ Google Fit Connect (auto-hides while OAuth not configured) ═══ */}
        <GoogleFitConnect language={language} onSync={() => fetchData()} />

        {/* ═══ Tabs ═══ */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="extra-miles-tabs">
          <TabBtn active={tab === 'overview'} onClick={() => setTab('overview')}>{fr ? 'Stats' : 'Stats'}</TabBtn>
          <TabBtn active={tab === 'challenge'} onClick={() => setTab('challenge')}>{fr ? 'Défi' : 'Challenge'}</TabBtn>
          <TabBtn active={tab === 'badges'} onClick={() => setTab('badges')}>{fr ? 'Badges' : 'Badges'}</TabBtn>
          <TabBtn active={tab === 'ranking'} onClick={() => setTab('ranking')}>{fr ? 'Classement' : 'Ranking'}</TabBtn>
        </div>

        {/* ═══ TAB: Overview ═══ */}
        {tab === 'overview' && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">

            {/* 7-Day Chart */}
            {history && history.history?.length > 0 && (
              <Card className="border-0 bg-card/60 shadow-lg" data-testid="steps-weekly-chart">
                <CardContent className="p-4">
                  <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground flex items-center gap-2 mb-3">
                    <CalendarDays className="w-3.5 h-3.5 text-blue-400" />
                    {fr ? 'Mes 7 derniers jours' : 'Last 7 days'}
                  </p>
                  <div className="flex items-end justify-between gap-1.5">
                    {history.history.map((day, i) => {
                      const dayLabel = new Date(day.date + 'T12:00:00').toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { weekday: 'narrow' });
                      const barPct = Math.min(100, (day.steps / GOAL) * 100);
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                          <span className="text-[8px] text-muted-foreground font-mono">{day.steps >= 1000 ? `${(day.steps / 1000).toFixed(0)}k` : day.steps || '-'}</span>
                          <div className="w-full relative" style={{ height: 60 }}>
                            <div className="absolute bottom-0 w-full bg-white/[0.03] rounded-md" style={{ height: 60 }} />
                            {/* 10k goal line */}
                            <div className="absolute w-full border-t border-dashed border-amber-500/30" style={{ bottom: '60%' }} />
                            <motion.div
                              className={`absolute bottom-0 w-full rounded-md ${day.goal_reached ? 'bg-gradient-to-t from-amber-500 to-amber-400' : 'bg-gradient-to-t from-blue-600 to-blue-500'}`}
                              initial={{ height: 0 }}
                              animate={{ height: `${barPct}%` }}
                              transition={{ duration: 0.5, delay: i * 0.06 }}
                              style={{ maxHeight: 60 }}
                            />
                          </div>
                          <span className="text-[9px] text-muted-foreground">{dayLabel}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between mt-2.5 text-[9px] text-muted-foreground">
                    <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {fr ? 'Moy.' : 'Avg.'} {history.average_steps?.toLocaleString()}</span>
                    <span className="flex items-center gap-1"><Flame className="w-3 h-3 text-amber-400" /> {history.days_goal_reached} {fr ? 'jours 10k+' : 'days 10k+'}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Key metrics grid */}
            <div className="grid grid-cols-2 gap-3">
              <MetricCard icon={TrendingUp} color="text-blue-400" label={fr ? 'Moy. 30j' : '30d avg'} value={`${monthStats.average >= 1000 ? `${(monthStats.average / 1000).toFixed(1)}k` : monthStats.average || 0}`} />
              <MetricCard icon={Target} color="text-emerald-400" label={fr ? 'Jours 10k (30j)' : '10k days (30d)'} value={`${monthStats.days_at_goal || 0}/${monthStats.days_logged || 0}`} />
              <MetricCard icon={MapPin} color="text-purple-400" label={fr ? 'Distance totale' : 'Total distance'} value={`${allTime.km_estimate || 0} km`} />
              <MetricCard icon={Zap} color="text-amber-400" label="XP" value={stats?.xp_points || 0} />
            </div>

            {/* Personal Record */}
            {pr.steps > 0 && (
              <Card className="border-0 bg-gradient-to-r from-purple-500/8 to-pink-500/5 shadow-lg">
                <CardContent className="p-3.5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center">
                    <Star className="w-5 h-5 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{fr ? 'Record personnel' : 'Personal record'}</p>
                    <p className="text-lg font-black">{pr.steps?.toLocaleString()} <span className="text-xs text-muted-foreground font-normal">{fr ? 'pas' : 'steps'}</span></p>
                  </div>
                  <span className="text-[10px] text-muted-foreground">{pr.date}</span>
                </CardContent>
              </Card>
            )}
          </motion.div>
        )}

        {/* ═══ TAB: Challenge (Défi de la Semaine) ═══ */}
        {tab === 'challenge' && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="challenge-section">
            {challenge ? (
              <>
                {/* Challenge Card */}
                <Card className={`border-0 shadow-xl overflow-hidden ${challenge.completed ? 'bg-gradient-to-br from-amber-500/10 via-card to-orange-500/5' : 'bg-gradient-to-br from-violet-500/8 via-card to-blue-500/5'}`} data-testid="weekly-challenge-card">
                  <CardContent className="p-5 space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${challenge.completed ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/20' : 'bg-gradient-to-br from-violet-500 to-blue-600 shadow-lg shadow-violet-500/20'}`}>
                          <Swords className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3 text-orange-500" />
                            {fr ? 'Défi de la semaine' : 'Weekly Challenge'}
                          </p>
                          <h3 className="font-heading text-lg uppercase tracking-tight" data-testid="challenge-name">
                            {fr ? challenge.name_fr : challenge.name_en}
                          </h3>
                        </div>
                      </div>
                      <div className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                        challenge.difficulty === 'easy' ? 'bg-emerald-500/15 text-emerald-400' :
                        challenge.difficulty === 'medium' ? 'bg-blue-500/15 text-blue-400' :
                        challenge.difficulty === 'hard' ? 'bg-orange-500/15 text-orange-400' :
                        'bg-red-500/15 text-red-400'
                      }`} data-testid="challenge-difficulty">
                        {challenge.difficulty === 'easy' ? (fr ? 'Facile' : 'Easy') :
                         challenge.difficulty === 'medium' ? (fr ? 'Moyen' : 'Medium') :
                         challenge.difficulty === 'hard' ? (fr ? 'Difficile' : 'Hard') :
                         (fr ? 'Extrême' : 'Extreme')}
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground" data-testid="challenge-desc">
                      {fr ? challenge.desc_fr : challenge.desc_en}
                    </p>

                    {/* Progress Bar */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{fr ? 'Progression' : 'Progress'}</span>
                        <span className="font-bold" data-testid="challenge-progress-text">
                          {challenge.progress?.current?.toLocaleString()} / {challenge.progress?.target?.toLocaleString()}
                          {challenge.type === 'days_at_goal' || challenge.type === 'days_logged' ? (fr ? ' jours' : ' days') : (fr ? ' pas' : ' steps')}
                        </span>
                      </div>
                      <div className="h-3 bg-white/[0.04] rounded-full overflow-hidden" data-testid="challenge-progress-bar">
                        <motion.div
                          className={`h-full rounded-full ${challenge.completed ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-violet-500 to-blue-500'}`}
                          initial={{ width: 0 }}
                          animate={{ width: `${challenge.progress?.pct || 0}%` }}
                          transition={{ duration: 1, ease: 'easeOut' }}
                        />
                      </div>
                      <p className="text-right text-[10px] text-muted-foreground font-mono">{challenge.progress?.pct || 0}%</p>
                    </div>

                    {/* XP Reward */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-border/20">
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-bold">{fr ? 'Récompense' : 'Reward'}</span>
                      </div>
                      <span className="text-sm font-black text-amber-400" data-testid="challenge-xp-reward">+{challenge.xp_reward} XP</span>
                    </div>

                    {/* Action Button */}
                    {challenge.completed && !challenge.claimed && (
                      <Button
                        onClick={claimChallenge}
                        disabled={claiming}
                        className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/20 font-heading uppercase tracking-wider"
                        size="lg"
                        data-testid="claim-challenge-btn"
                      >
                        {claiming ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Gift className="w-5 h-5 mr-2" />}
                        {fr ? 'Réclamer la récompense' : 'Claim Reward'}
                      </Button>
                    )}

                    {challenge.claimed && (
                      <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20" data-testid="challenge-claimed-badge">
                        <Check className="w-5 h-5 text-emerald-400" />
                        <span className="text-sm font-bold text-emerald-400">{fr ? 'Récompense réclamée!' : 'Reward claimed!'}</span>
                      </div>
                    )}

                    {!challenge.completed && (
                      <p className="text-center text-[10px] text-muted-foreground italic">
                        {fr ? 'Continue tes pas pour compléter le défi!' : 'Keep walking to complete the challenge!'}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Week info */}
                <p className="text-[10px] text-muted-foreground text-center">
                  {fr ? `Semaine du ${challenge.week_id}` : `Week of ${challenge.week_id}`}
                </p>
              </>
            ) : (
              <div className="text-center py-12 space-y-3">
                <Swords className="w-12 h-12 text-muted-foreground/20 mx-auto" />
                <p className="text-sm text-muted-foreground">{fr ? 'Aucun défi disponible' : 'No challenge available'}</p>
              </div>
            )}
          </motion.div>
        )}

        {/* ═══ TAB: Badges ═══ */}
        {tab === 'badges' && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="badges-section">
            <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
              {fr ? `${earned.length}/${available.length} badges debloques` : `${earned.length}/${available.length} badges unlocked`}
            </p>
            {available.map((badge) => {
              const isEarned = earnedIds.has(badge.id);
              const Icon = BADGE_ICONS[badge.icon] || Trophy;
              const earnedData = earned.find(b => b.id === badge.id);
              return (
                <motion.div key={badge.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} data-testid={`badge-${badge.id}`}>
                  <Card className={`border-0 shadow-lg transition-all ${isEarned ? 'bg-card/80' : 'bg-card/30 opacity-60'}`}>
                    <CardContent className="p-3.5 flex items-center gap-3.5">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isEarned ? `bg-gradient-to-br ${TIER_COLORS[badge.tier]} ring-2 ${TIER_RING[badge.tier]}` : 'bg-muted/20'}`}>
                        {isEarned ? <Icon className="w-6 h-6 text-white" /> : <Lock className="w-5 h-5 text-muted-foreground/40" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold ${isEarned ? '' : 'text-muted-foreground'}`}>{fr ? badge.name_fr : badge.name}</p>
                        <p className="text-[10px] text-muted-foreground">{fr ? badge.desc_fr : badge.desc}</p>
                        {isEarned && earnedData?.awarded_at && (
                          <p className="text-[9px] text-emerald-400 mt-0.5">{fr ? 'Debloque le' : 'Unlocked'} {new Date(earnedData.awarded_at).toLocaleDateString(fr ? 'fr-CA' : 'en-CA')}</p>
                        )}
                      </div>
                      {isEarned && <Check className="w-5 h-5 text-emerald-400 shrink-0" />}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* ═══ TAB: Ranking ═══ */}
        {tab === 'ranking' && leaderboard && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">

            {/* My rank card */}
            <Card className="border-0 bg-gradient-to-br from-blue-500/8 via-card to-indigo-500/5 shadow-lg">
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-2xl font-black">{leaderboard.my_rank === 'hidden' ? '--' : (leaderboard.my_rank || '--')}</p>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-widest">{fr ? 'Mon rang' : 'My rank'}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black">{leaderboard.my_total_steps >= 1000 ? `${(leaderboard.my_total_steps / 1000).toFixed(1)}k` : leaderboard.my_total_steps}</p>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-widest">{fr ? 'Pas semaine' : 'Week steps'}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black">{history?.days_goal_reached || 0}<span className="text-sm text-muted-foreground">/7</span></p>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-widest">{fr ? 'Jours 10k' : '10k days'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Privacy toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-card/40 border border-border/30">
              <div className="flex items-center gap-2">
                {isVisible ? <Eye className="w-4 h-4 text-emerald-400" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                <div>
                  <Label className="text-xs font-semibold">{fr ? 'Visible dans le classement' : 'Visible in leaderboard'}</Label>
                  <p className="text-[9px] text-muted-foreground">{fr ? 'Les autres verront tes pas' : 'Others see your steps'}</p>
                </div>
              </div>
              <Switch checked={isVisible} onCheckedChange={toggleVisibility} disabled={toggling} data-testid="leaderboard-privacy-toggle" />
            </div>

            {/* Week dates */}
            <p className="text-[10px] text-muted-foreground text-center">
              {fr ? `Semaine du ${leaderboard.week_start} au ${leaderboard.week_end}` : `Week of ${leaderboard.week_start} to ${leaderboard.week_end}`}
            </p>

            {/* Leaderboard list */}
            <div className="space-y-2" data-testid="leaderboard-list">
              {leaderboard.leaderboard?.length > 0 ? leaderboard.leaderboard.map((entry, i) => {
                const maxSteps = leaderboard.leaderboard[0]?.total_steps || 1;
                const barW = Math.max(5, (entry.total_steps / maxSteps) * 100);
                return (
                  <motion.div key={entry.user_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
                    <div className={`relative flex items-center gap-3 p-3 rounded-xl border overflow-hidden ${getRankBg(entry.rank)} ${entry.is_current_user ? 'ring-1 ring-primary/30' : ''}`} data-testid={`leaderboard-entry-${entry.rank}`}>
                      {/* Background bar */}
                      <div className="absolute inset-y-0 left-0 bg-blue-500/[0.04] rounded-xl" style={{ width: `${barW}%` }} />
                      <div className="relative flex items-center gap-3 w-full">
                        <RankIcon rank={entry.rank} />
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-bold shrink-0 overflow-hidden">
                          {entry.avatar_url ? <img src={entry.avatar_url} alt="" className="w-full h-full object-cover" /> : entry.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-bold truncate ${entry.is_current_user ? 'text-primary' : ''}`}>{entry.name} {entry.is_current_user ? (fr ? '(toi)' : '(you)') : ''}</p>
                          <p className="text-[9px] text-muted-foreground">{entry.days_logged} {fr ? 'jours' : 'days'}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-black">{entry.total_steps >= 1000 ? `${(entry.total_steps / 1000).toFixed(1)}k` : entry.total_steps}</p>
                          <p className="text-[8px] text-muted-foreground uppercase">{fr ? 'pas' : 'steps'}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              }) : (
                <div className="text-center py-12 space-y-3">
                  <Footprints className="w-12 h-12 text-muted-foreground/20 mx-auto" />
                  <p className="text-sm text-muted-foreground">{fr ? 'Aucun pas enregistré cette semaine. Sois le premier!' : 'No steps logged this week. Be the first!'}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

      </div>
    </Layout>
  );
}

/* ─── Metric card helper ─── */
function MetricCard({ icon: Icon, color, label, value }) {
  return (
    <Card className="border-0 bg-card/50 shadow-lg">
      <CardContent className="p-3 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-white/[0.04] flex items-center justify-center shrink-0">
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <div className="min-w-0">
          <p className="text-[9px] text-muted-foreground uppercase tracking-widest">{label}</p>
          <p className="text-base font-black truncate">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
